"use client"

import React from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { cn } from '../../../lib/utils'
import { ComparisonData } from '../types'
import { ArrowUpRight, ArrowDownRight, Minus, BarChart3 } from 'lucide-react'
import { Badge } from '../../ui/badge'

interface ComparisonWidgetProps {
  data: ComparisonData
  title?: string
  className?: string
  glassmorphism?: boolean
  compact?: boolean
}

export function ComparisonWidget({
  data,
  title = "Comparison",
  className,
  glassmorphism = false,
  compact = false
}: ComparisonWidgetProps) {
  const [isMounted, setIsMounted] = React.useState(false)

  React.useEffect(() => {
    setIsMounted(true)
  }, [])

  // En yüksek değeri bul
  const maxValue = Math.max(...data.periods.map(p => p.value))
  
  // İlk iki period arasındaki değişimi hesapla
  const change = data.periods.length >= 2 
    ? ((data.periods[0].value - data.periods[1].value) / data.periods[1].value) * 100
    : 0
    
  const changeType = change > 0 ? 'increase' : change < 0 ? 'decrease' : 'neutral'

  // Değer formatla
  const formatValue = (value: number) => {
    if (!isMounted) return value.toString()
    
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M'
    } else if (value >= 1000) {
      return (value / 1000).toFixed(0) + 'K'
    }
    return value.toLocaleString()
  }

  return (
    <Card className={cn(
      "h-full flex flex-col overflow-hidden",
      glassmorphism && "bg-background/60 backdrop-blur-md border-white/10",
      className
    )}>
      <CardHeader className="flex-shrink-0 pb-3 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{title}</span>
            </CardTitle>
            {data.metric && (
              <p className="text-xs text-muted-foreground mt-1 truncate">{data.metric}</p>
            )}
          </div>
          {change !== 0 && (
            <div 
              className={cn(
                "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium flex-shrink-0",
                changeType === 'increase' && "bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20",
                changeType === 'decrease' && "bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20",
                changeType === 'neutral' && "bg-muted text-muted-foreground border border-border"
              )}
            >
              {changeType === 'increase' ? <ArrowUpRight className="h-3 w-3" /> :
               changeType === 'decrease' ? <ArrowDownRight className="h-3 w-3" /> :
               <Minus className="h-3 w-3" />}
              <span>
                {Math.abs(change).toFixed(1)}%
              </span>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 px-4 pb-4 overflow-hidden flex flex-col gap-3">
        {/* Bar list görünümü - scrollable area */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-2 -mr-2">
          <div className="space-y-2.5">
            {data.periods.map((period, index) => {
              const percentage = (period.value / maxValue) * 100
              const isHighest = period.value === maxValue
              
              return (
                <motion.div
                  key={period.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {/* Label ve değer */}
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span className={cn(
                      "text-xs font-medium truncate flex-1",
                      isHighest ? "text-foreground" : "text-muted-foreground"
                    )}>
                      {period.label}
                    </span>
                    <span className={cn(
                      "text-xs font-semibold tabular-nums flex-shrink-0",
                      isHighest && "text-primary"
                    )}>
                      {formatValue(period.value)}
                    </span>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                    <motion.div
                      className={cn(
                        "h-full rounded-full transition-colors",
                        isHighest ? "bg-primary" : "bg-primary/60"
                      )}
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(percentage, 100)}%` }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                    />
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
        
        {/* Compact chart view - fixed height */}
        {data.showChart && (
          <div className="flex-shrink-0 pt-3 border-t">
            <div className="h-14 flex items-end justify-between gap-1">
              {data.periods.slice(0, 5).map((period, index) => {
                const height = (period.value / maxValue) * 100
                const isHighest = period.value === maxValue
                
                return (
                  <motion.div
                    key={period.label}
                    className="flex-1 flex flex-col items-center gap-0.5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="w-full h-10 flex items-end justify-center px-0.5">
                      <motion.div
                        className={cn(
                          "w-full max-w-[20px] rounded-t transition-colors",
                          isHighest ? "bg-primary" : "bg-primary/40"
                        )}
                        initial={{ height: 0 }}
                        animate={{ height: `${height}%` }}
                        transition={{ duration: 0.5, delay: index * 0.05 }}
                      />
                    </div>
                    <span className="text-[9px] text-muted-foreground">
                      {period.label.slice(0, 3)}
                    </span>
                  </motion.div>
                )
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}